import React from 'react';
import { Investigation, InvestigationStatus, InvestigationPriority } from '../types';
import { TablePagination, useTablePagination } from '../../../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../../../shared/useTableSort';

interface InvestigationsTableProps {
  investigations: Investigation[];
  loading: boolean;
  onSelect: (investigation: Investigation) => void;
  onRefresh: () => void;
}

export const InvestigationsTable: React.FC<InvestigationsTableProps> = ({
  investigations,
  loading,
  onSelect,
}) => {
  const {
    sortedItems,
    sortField,
    sortDirection,
    requestSort,
  } = useTableSort(investigations);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = useTablePagination(sortedItems, 25);
  const getStatusBadge = (status: InvestigationStatus) => {
    switch (status) {
      case 'DRAFT':
        return <span className="badge-pill badge-neutral">Draft</span>;
      case 'INITIATED':
        return <span className="badge-pill badge-info">Initiated</span>;
      case 'INVESTIGATOR_ASSIGNED':
        return <span className="badge-pill badge-info">Assigned</span>;
      case 'EVIDENCE_COLLECTION':
        return <span className="badge-pill badge-warning">Evidence Collection</span>;
      case 'ROOT_CAUSE_ANALYSIS':
        return <span className="badge-pill badge-warning">Root Cause Analysis</span>;
      case 'DRAFT_REPORT':
        return <span className="badge-pill badge-info">Draft Report</span>;
      case 'PENDING_REVIEW':
        return <span className="badge-pill badge-warning">Pending Review</span>;
      case 'APPROVED':
        return <span className="badge-pill badge-success">Approved</span>;
      case 'PENDING_ACTIONS':
        return <span className="badge-pill badge-warning">Pending Actions</span>;
      case 'CLOSED':
        return <span className="badge-pill badge-neutral">Closed</span>;
      case 'REOPENED':
        return <span className="badge-pill badge-danger">Reopened</span>;
      default:
        return <span className="badge-pill badge-neutral">{status.replace(/_/g, ' ')}</span>;
    }
  };

  const getPriorityBadge = (priority: InvestigationPriority) => {
    switch (priority) {
      case 'CRITICAL':
        return <span className="badge-pill badge-danger">Critical</span>;
      case 'HIGH':
        return <span className="badge-pill badge-warning">High</span>;
      case 'MEDIUM':
        return <span className="badge-pill badge-info">Medium</span>;
      case 'LOW':
        return <span className="badge-pill badge-neutral">Low</span>;
      default:
        return <span className="badge-pill badge-neutral">{priority}</span>;
    }
  };

  if (!loading && investigations.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
        <p style={{ margin: 0, fontSize: '15px' }}>No investigation cases found for the selected view.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <SortableTh field="investigationNumber" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Case Number
              </SortableTh>
              <SortableTh field="title" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Title & Scope
              </SortableTh>
              <SortableTh field="type" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Type
              </SortableTh>
              <SortableTh field="priority" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Priority
              </SortableTh>
              <SortableTh field="leadInvestigatorName" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Lead Investigator
              </SortableTh>
              <SortableTh field="targetCompletionDate" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                SLA Target Date
              </SortableTh>
              <SortableTh field="status" sortField={sortField} sortDirection={sortDirection} onSort={requestSort}>
                Status
              </SortableTh>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.map((inv) => {
              const isOverdue = inv.status !== 'CLOSED' && new Date(inv.targetCompletionDate).getTime() < Date.now();

              return (
                <tr
                  key={inv.id}
                  onClick={() => onSelect(inv)}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ fontWeight: 600, color: '#9333ea', fontFamily: 'monospace' }}>
                    {inv.investigationNumber}
                  </td>
                  <td style={{ maxWidth: '300px' }}>
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span>{inv.title}</span>
                      {(inv.linkedReportNumber || inv.linkedReportId) && (
                        <span
                          className="badge-pill"
                          style={{
                            background: '#e0e7ff',
                            color: '#4338ca',
                            fontSize: '10px',
                            fontWeight: 700,
                            padding: '1px 6px',
                          }}
                        >
                          📄 Report: {inv.linkedReportNumber || inv.linkedReportId}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        color: '#64748b',
                        fontSize: '12px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {inv.summary || (inv.occurrenceNumber ? `Linked to ${inv.occurrenceNumber}` : (inv.linkedReportNumber ? `Origin Report: ${inv.linkedReportNumber}` : 'Standalone case'))}
                    </div>
                  </td>
                  <td style={{ color: '#334155', fontWeight: 500 }}>
                    {inv.type}
                  </td>
                  <td>
                    {getPriorityBadge(inv.priority)}
                  </td>
                  <td style={{ color: '#1e293b' }}>
                    {inv.leadInvestigatorName || (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Unassigned</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ color: isOverdue ? '#dc2626' : '#475569', fontWeight: isOverdue ? 700 : 400, fontSize: '12px' }}>
                        {new Date(inv.targetCompletionDate).toLocaleDateString()}
                      </span>
                      {isOverdue && (
                        <span className="badge-pill badge-danger" style={{ fontSize: '10px' }}>
                          OVERDUE
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(inv.status)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(inv);
                      }}
                      className="btn-secondary"
                      style={{ width: 'auto', padding: '5px 12px', fontSize: '12px' }}
                    >
                      Workspace
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        itemLabel="investigations"
      />
    </div>
  );
};
